import { notificationService } from '@shared/services/notification-service';
import { renderInputHtml } from '@features/service-form/field-renderer';
import type {
  RepeatingGroupConfig,
  GroupFieldConfig,
} from '@features/service-form/service-form-types';

export class RepeatingFieldGroup {
  private config: Required<Omit<RepeatingGroupConfig, 'onChange'>> &
    Pick<RepeatingGroupConfig, 'onChange'>;
  private container: HTMLElement;
  private itemsContainer!: HTMLElement;
  private addButton!: HTMLButtonElement;

  constructor(config: RepeatingGroupConfig) {
    this.config = {
      min: 0,
      max: Infinity,
      addLabel: '新增',
      removeLabel: '删除',
      ...config,
    };

    const el = document.getElementById(config.containerId);
    if (!el) throw new Error(`RepeatingFieldGroup: container #${config.containerId} not found`);
    this.container = el;

    this.renderShell();
    this.itemsContainer = this.container.querySelector<HTMLElement>('.ea-rg-items')!;
    this.addButton = this.container.querySelector<HTMLButtonElement>('[data-add-btn]')!;
    this.bindEvents();

    for (let i = 0; i < this.config.min; i++) {
      this.addItem({}, true);
    }
  }

  private renderShell(): void {
    this.container.innerHTML = `
      <div class="ea-rg-items space-y-4"></div>
      <div class="flex justify-end pt-3">
        <button
          type="button"
          data-add-btn
          class="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1 transition-colors"
        >
          <span aria-hidden="true">+</span>
          <span>${this.config.addLabel}</span>
        </button>
      </div>
    `;
  }

  private bindEvents(): void {
    this.addButton.addEventListener('click', () => this.addItem());

    this.itemsContainer.addEventListener('click', e => {
      const btn = (e.target as HTMLElement).closest('[data-remove-btn]');
      if (!btn) return;
      const item = btn.closest('[data-group-item]');
      if (!item) return;
      const items = Array.from(this.itemsContainer.querySelectorAll('[data-group-item]'));
      const index = items.indexOf(item as HTMLElement);
      if (index !== -1) this.removeItem(index);
    });

    this.itemsContainer.addEventListener('input', () => this.config.onChange?.());
    this.itemsContainer.addEventListener('change', () => this.config.onChange?.());
  }

  addItem(data: Record<string, string> = {}, silent = false): void {
    if (this.getCount() >= this.config.max) {
      if (!silent) notificationService.warning(`最多只能添加 ${this.config.max} 条`);
      return;
    }

    const item = document.createElement('div');
    item.className = 'ea-rg-item bg-gray-50 p-4 rounded-lg space-y-4 relative';
    item.setAttribute('data-group-item', '');
    item.innerHTML = `
      <button
        type="button"
        data-remove-btn
        class="absolute top-2 right-2 text-red-500 hover:text-red-700 text-xs px-2 py-1 rounded transition-colors"
      >${this.config.removeLabel}</button>
      <div class="ea-service-form-grid ea-service-form-grid--two">
        ${this.config.fields.map(f => this.renderField(f, data[f.name] ?? '')).join('')}
      </div>
    `;

    this.itemsContainer.appendChild(item);
    this.syncControls();
    if (!silent) this.config.onChange?.();
  }

  private renderField(field: GroupFieldConfig, value: string): string {
    const { name, type, label, placeholder, required, options, span } = field;
    const wrapperClass = span === 'full' ? 'ea-service-form-span-2' : '';

    const inputHtml = renderInputHtml({
      name: `${name}[]`,
      type,
      value,
      placeholder,
      required,
      options,
      inputClass: 'ea-service-form-input',
      textareaClass: 'ea-service-form-input ea-service-form-textarea',
      selectClass: 'ea-service-form-input ea-service-form-select',
    });

    if (!label) return `<div class="${wrapperClass}">${inputHtml}</div>`;

    return `<div class="${wrapperClass}">
      <label class="ea-service-form-label">${label}</label>
      ${inputHtml}
    </div>`;
  }

  removeItem(index: number): void {
    if (this.getCount() <= this.config.min) return;
    const items = this.itemsContainer.querySelectorAll('[data-group-item]');
    const item = items[index];
    if (item) {
      item.remove();
      this.syncControls();
      this.config.onChange?.();
    }
  }

  private syncControls(): void {
    const count = this.getCount();
    const atMin = count <= this.config.min;
    const atMax = count >= this.config.max;

    this.itemsContainer.querySelectorAll('[data-group-item]').forEach(item => {
      const btn = item.querySelector<HTMLButtonElement>('[data-remove-btn]');
      if (!btn) return;
      btn.disabled = atMin;
      btn.classList.toggle('hidden', atMin);
    });

    this.addButton.disabled = atMax;
    this.addButton.classList.toggle('opacity-50', atMax);
    this.addButton.classList.toggle('cursor-not-allowed', atMax);
  }

  getItems(): Record<string, string>[] {
    const items = this.itemsContainer.querySelectorAll('[data-group-item]');
    return Array.from(items).map(item => {
      const row: Record<string, string> = {};
      this.config.fields.forEach(field => {
        const el = item.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
          `[name="${field.name}[]"]`
        );
        row[field.name] = el?.value ?? '';
      });
      return row;
    });
  }

  setItems(rows: Record<string, string>[]): void {
    this.itemsContainer.innerHTML = '';
    const limited = rows.slice(0, this.config.max === Infinity ? rows.length : this.config.max);
    limited.forEach(row => this.addItem(row, true));
    this.syncControls();
  }

  getFieldNames(): string[] {
    return this.config.fields.map(f => f.name);
  }

  getCount(): number {
    return this.itemsContainer.querySelectorAll('[data-group-item]').length;
  }

  destroy(): void {
    this.container.innerHTML = '';
  }
}
